import React, { useState } from "react";

import { AlertType } from "@constants";
import { Alert, Icon, Loader } from "@partials";
import { errorLogger, getLocalization, getCSRFToken } from "@utilities";

import { RateVariationWidget as Props, RateVariationWidgetLocalization } from "./types";
import List from "./components/list";

const RateVariation: React.FC<Props> = ({
  api,
  data,
  localization,
  backButtonUrl,
  configuration,
  cultureCode,
  date,
  isQueryDate,
  iconsList,
}) => {
  const { calendar } = iconsList;

  // FORM
  const [queryDateValue, setQueryDate] = useState<boolean>(isQueryDate); // boolean to show 'date error message' if data are not available for selected day
  const [queryDateText, setQueryDateText] =
    useState<keyof RateVariationWidgetLocalization>("releaseTimeNoRecord"); // text for 'date error message'
  const [inputQueryDate, setInputQueryDate] = useState(date);

  // RESULTS
  const [isLoaded, setIsLoaded] = useState<boolean>(true);
  const [items, setItems] = useState(data);
  const [hasResultsError, setHasResultsError] = useState<boolean>(false);

  const config = {
    date: inputQueryDate,
    cultureCode,
    configuration,
  };

  // DATEPICKER INPUT
  // year
  const TEN_YEARS = 10;
  const currentYear = new Date().getFullYear();
  const tenYearsInThePast = currentYear - TEN_YEARS;

  // month
  const month = new Date().getMonth() + 1;
  const monthString = `0${month}`.slice(-2);

  // day
  const day = new Date().getDate();
  const dayString = `0${day.toString()}`.slice(-2);

  // values: min, max
  const datePickerMinValue = `${tenYearsInThePast}-01-01`;
  const datePickerMaxValue = `${currentYear}-${monthString}-${dayString}`;

  const changeDate = (changedTime) => {
    setInputQueryDate(changedTime);
  };

  const fetchData = async () => {
    const response = await fetch(api, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        __RequestVerificationToken: getCSRFToken(),
      },
      body: JSON.stringify(config),
    });
    if (!response.ok) {
      setHasResultsError(true);
      setIsLoaded(true);
      throw new Error("Data coud not be fetched!");
    } else {
      setIsLoaded(true);
      return response.json();
    }
  };

  const loadData = () => {
    setIsLoaded(false);
    setHasResultsError(false);
    fetchData()
      .then((res) => {
        if (res.payload.data.length > 0) {
          setItems(res.payload.data);
          setInputQueryDate(res.payload.responseDate);
          setQueryDate(res.payload.requestDateNotAvailable);
          setQueryDateText(res.payload.localization.releaseTimeNoRecord);
          window.history.replaceState(null, "", `?date=${res.payload.responseDate}`);
        } else {
          setHasResultsError(true);
        }
      })
      .catch((error) => {
        if (!isProduction) {
          errorLogger(error);
        }
        setHasResultsError(true);
      });
  };

  // DATEPICKER
  const hasDateError = queryDateValue;
  return (
    <div className="container">
      <div className="-mx-8 mb-12 bg-white p-8 pb-12 shadow-tab sm:mx-0 sm:rounded sm:py-12 sm:px-0">
        <div className="row">
          <div className="sm:col-14 sm:offset-1">
            <div className={hasDateError ? "sm:flex sm:items-center" : "sm:flex sm:items-end"}>
              <div className="w-full">
                <div className="mb-1.5 text-2xs">{getLocalization(localization, "DatePicker")}</div>
                <label htmlFor="dataSelect" className="mb-0 block sm:mr-8">
                  <div className="relative">
                    <input
                      type="date"
                      id="dataSelect"
                      name="dataSelect"
                      className="date-picker-rates"
                      min={datePickerMinValue}
                      max={datePickerMaxValue}
                      value={inputQueryDate}
                      onChange={(event) => {
                        changeDate(event.target.value);
                      }}
                    />

                    <div className="pointer-events-none absolute right-[3px] top-[10px] w-[32px] bg-white">
                      <Icon
                        name={calendar.name}
                        viewBox={calendar.viewbox}
                        alt={calendar.alt}
                        svg={calendar.svg}
                        className="inline h-[21.5px] w-[19.5px] bg-white fill-red-corporate"
                      />
                    </div>
                  </div>
                </label>
                {hasDateError && (
                  <div className="mb-6 text-xs text-red-corporate sm:mb-0">
                    {getLocalization(localization, queryDateText)}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={loadData}
                className={`btn btn--primary ${hasDateError ? "mt-0" : "mt-6 sm:mt-0"}`}
              >
                {getLocalization(localization, "ShowButton")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {isLoaded ? (
        <div className="row">
          <div className="sm:col-14 sm:offset-1">
            {hasResultsError ? (
              <Alert
                type={AlertType.Error}
                text={getLocalization(localization, "ErrorMessage")}
                textAttributes={{ role: "alert", "aria-live": "assertive" }}
                className="mb-12"
              />
            ) : (
              <List items={items} localization={localization} />
            )}
          </div>
        </div>
      ) : (
        <Loader className="mb-12" />
      )}
      <div className="row">
        <div className="sm:col-14 sm:offset-1">
          <a role="button" href={backButtonUrl} className="btn btn--secondary mb-12 inline-block">
            {getLocalization(localization, "BackButton")}
          </a>
        </div>
      </div>
    </div>
  );
};

export default RateVariation;
